import { CSSProperties, useEffect, useState } from "react";
import { Body2 } from "components/shareable/Typography";
import Colors from "modules/Colors";
import { CURRENCY_SYMBOLS, DEFAULT_CURRENCY } from "modules/Const";
import { CurrencyCodesType } from "modules/Types";
import { Container } from "modules/Sty";
import { clsx } from "modules/Utils";
import InputSymphony, { SelectOptionItem } from "elements/InputSymphony";

/** Common currencies that should appear at the top of the dropdown */
const COMMON_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

/**
 * Maps currency codes and symbols into SelectOptionItem format, with common currencies appearing first
 * @returns Array of SelectOptionItem objects for the currency dropdown
 */
const mapCurrencyItems = (): SelectOptionItem[] => {
    const allCurrencies = Object.keys(CURRENCY_SYMBOLS);

    // Separate common and other currencies
    const commonCurrencyItems = COMMON_CURRENCIES
        .filter(code => allCurrencies.includes(code))
        .map(code => ({
            value: `${code} (${CURRENCY_SYMBOLS[code as CurrencyCodesType]})`,
            title: `${code} (${CURRENCY_SYMBOLS[code as CurrencyCodesType]})`
        }));

    const otherCurrencyItems = allCurrencies
        .filter(code => !COMMON_CURRENCIES.includes(code))
        .map(code => ({
            value: `${code} (${CURRENCY_SYMBOLS[code as CurrencyCodesType]})`,
            title: `${code} (${CURRENCY_SYMBOLS[code as CurrencyCodesType]})`
        }));

    return [...commonCurrencyItems, ...otherCurrencyItems];
}

interface PublicCurrencyDropdownProps {
    className?: string;
    style?: CSSProperties;
    initialCurrency: string;
    hideTitle?: boolean;
    onCurrencyChange: (currencyCode: string) => void;
}

const PublicCurrencyDropdown = ({
    className = '',
    style = {},
    initialCurrency,
    hideTitle = false,
    onCurrencyChange,
}: PublicCurrencyDropdownProps) => {
    const [selectedCurrency, setSelectedCurrency] = useState<string>(initialCurrency);

    const handleOnSelectCurrency = (option: SelectOptionItem) => {
        const [currencyCode] = option.value.split('(');
        const currencyTypeValue = currencyCode.trim();
        
        setSelectedCurrency(`${currencyTypeValue} (${CURRENCY_SYMBOLS[currencyTypeValue as CurrencyCodesType]})`);
        onCurrencyChange(currencyTypeValue);
    };

    useEffect(() => {
        if (selectedCurrency !== initialCurrency) {
            setSelectedCurrency(`${initialCurrency} (${CURRENCY_SYMBOLS[initialCurrency as CurrencyCodesType]})`);
        }
    }, [initialCurrency]);

    // Format the initial currency display value
    useEffect(() => {
        const formattedCurrency = `${initialCurrency} (${CURRENCY_SYMBOLS[initialCurrency as CurrencyCodesType]})`;
        setSelectedCurrency(formattedCurrency);
    }, []);

    return (
        <Container
            className={clsx('publicCurrencyDropdownComponent', className)}
            padding={style.padding?.toString() || "0px"}
            gap={8}
            display="flex"
            flexDirection={style.flexDirection || "column"}
        >
            {!hideTitle &&
                <Body2 color={Colors.textColor}>
                    Currency
                </Body2>
            }
            <InputSymphony
                className="publicCurrencyDropdown"
                isSelectDropdown={true}
                options={mapCurrencyItems()}
                value={selectedCurrency}
                onOptionSelect={handleOnSelectCurrency}
                placeholder="Select currency"
            />
        </Container>
    );
};

export default PublicCurrencyDropdown; 